import { createSignal, Show } from 'solid-js'
import { ConsoleLog } from '@/components/ConsoleLog/ConsoleLog'
import { GPUWEB_IMPL_STATUS_URL } from '@/components/ErrorHandling/PreviewPoster'
import { GitHub } from '@/icons'
import { GIT_SHA, VERSION } from '@/version'
import ui from './ErrorHandling.module.css'

export function WebgpuNotSupported() {
  return (
    <div class={ui.fallback}>
      <h1 class={ui.webgpuTitle}>CHAOS MASTER</h1>

      <p class={ui.webgpuText}>
        Your browser or device currently does not support{' '}
        <strong>WebGPU</strong>.
      </p>

      <a
        href={GPUWEB_IMPL_STATUS_URL}
        target="_blank"
        rel="noopener noreferrer"
        class={ui.webgpuBtn}
      >
        Check WebGPU Browser Support
      </a>

      <div class={ui.webgpuHint}>
        Try the latest Chrome, Firefox, or Safari (on supported hardware) for
        the full experience.
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Device metadata for crash bug reports
// ---------------------------------------------------------------------------

interface MetadataEntry {
  label: string
  value: string
}

function gatherDeviceMetadata(): MetadataEntry[] {
  const entries: MetadataEntry[] = []
  const { navigator, screen } = globalThis

  // App
  entries.push({
    label: 'App Version',
    value: `${VERSION}${GIT_SHA ? ` (${GIT_SHA})` : ''}`,
  })

  // Browser / OS
  entries.push({ label: 'User Agent', value: navigator.userAgent })

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  entries.push({ label: 'Platform', value: navigator.platform })
  entries.push({ label: 'Language', value: navigator.language })

  // Screen
  entries.push({
    label: 'Screen',
    // eslint-disable-next-line no-restricted-globals
    value: `${screen.width}x${screen.height} @ ${devicePixelRatio}x`,
  })
  entries.push({
    label: 'Viewport',
    value: `${window.innerWidth}x${window.innerHeight}`,
  })

  // WebGPU
  entries.push({
    label: 'WebGPU',
    value: 'gpu' in navigator ? 'Supported' : 'Not supported',
  })

  // Memory (Chrome-only)
  // eslint-disable-next-line no-restricted-globals
  const mem = performance.memory
  if (mem) {
    const mb = (n: number) => `${(n / 1024 / 1024).toFixed(0)} MB`
    entries.push({
      label: 'JS Heap',
      value: `${mb(mem.usedJSHeapSize)} / ${mb(mem.totalJSHeapSize)} (limit ${mb(mem.jsHeapSizeLimit)})`,
    })
  }
  const deviceMemory = navigator.deviceMemory
  if (deviceMemory !== undefined) {
    entries.push({ label: 'Device Memory', value: `~${deviceMemory} GB` })
  }

  // Hardware concurrency
  if (navigator.hardwareConcurrency) {
    entries.push({
      label: 'CPU Cores',
      value: String(navigator.hardwareConcurrency),
    })
  }

  return entries
}

function formatMetadataText(entries: MetadataEntry[]): string {
  const maxLabel = Math.max(...entries.map((e) => e.label.length))
  return entries
    .map((e) => `${e.label.padEnd(maxLabel)}  ${e.value}`)
    .join('\n')
}

function DeviceMetadata() {
  const entries = gatherDeviceMetadata()
  const [copied, setCopied] = createSignal(false)
  const [open, setOpen] = createSignal(false)

  function copyMetadata(e: MouseEvent) {
    e.stopPropagation()
    const text = formatMetadataText(entries)
    // eslint-disable-next-line no-restricted-globals
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <div class={ui.metadataSection}>
      <div class={ui.metadataHeader} onClick={() => setOpen((v) => !v)}>
        <span class={ui.metadataTitle}>
          <span
            class={ui.collapseIndicator}
            classList={{ [ui.collapseOpen!]: open() }}
          />
          Device Info
        </span>
        <button class={ui.metadataCopyBtn} onClick={copyMetadata}>
          {copied() ? 'Copied!' : 'Copy Device Info'}
        </button>
      </div>
      <Show when={open()}>
        <div class={ui.metadataGrid}>
          {entries.map((entry) => (
            <>
              <span class={ui.metadataLabel}>{entry.label}</span>
              <span class={ui.metadataValue}>{entry.value}</span>
            </>
          ))}
        </div>
      </Show>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Recovery actions for crash screen
// ---------------------------------------------------------------------------

function reloadPage() {
  window.location.reload()
}

function reloadAndClearStorage() {
  const confirmed = confirm(
    'This will clear all local data including recent flames, settings, and preferences.\n\nAre you sure?',
  )
  if (confirmed) {
    try {
      localStorage.clear()
      sessionStorage.clear()
    } catch {
      // Incognito mode may throw
    }
    // Also attempt to clear IndexedDB databases
    if (window.indexedDB?.databases) {
      void window.indexedDB.databases().then((dbs) => {
        for (const db of dbs) {
          if (db.name) window.indexedDB.deleteDatabase(db.name)
        }
      })
    }
    window.location.reload()
  }
}

export function AppCrashed() {
  return (
    <div class={ui.crashOverlay}>
      <div class={ui.crashCard}>
        <div class={ui.crashHero}>
          <h1 class={ui.crashTitle}>CHAOS MASTER</h1>
          <div class={ui.crashBadgeRow}>
            <span class={ui.crashVersionBadge}>v{VERSION}</span>
            {GIT_SHA ? <span class={ui.crashShaBadge}>{GIT_SHA}</span> : null}
          </div>
          <p class={ui.crashSubtitle}>
            Something went wrong. See the logs below for details.
          </p>
        </div>
        <div class={ui.crashBody}>
          <DeviceMetadata />
          <ConsoleLog collapsible defaultOpen variant="crash" />
          <div class={ui.crashActions}>
            <a
              class={ui.crashGithubBtn}
              href="https://github.com/chaos-matters/chaos-master/issues/new?template=bug_report.md"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GitHub />
              Report Bug
            </a>
            <button class={ui.reloadBtn} onClick={reloadPage}>
              Reload Page
            </button>
            <button class={ui.reloadClearBtn} onClick={reloadAndClearStorage}>
              Reload + Clear Data
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
